import React, { useEffect, useRef, useState } from 'react';
import API, { PrivateMeetingSessionDetailsResponseType, PROTECTED } from '../../../../lib/api';
import { EventType } from '../../SessionWrapper/LayoutWrapper';
import { JOIN_STATUS } from '../../../../lib/constants';
import { showJoinButton } from '../../SessionWrapper/helpers';
import useConnectMeetingDisabled from '../../../../hooks/use-connect-meeting-disabled';
import MeetingStatus from '../MeetingStatus';
import { AddToCalendarButton, JoinButton, RegisterButton, ViewDetailsButton } from './CardButtonPresentation';
import { useOpenMeetingRegistration } from '../../../../hooks/use-open-meeting-registration';
import { isMeetingRegistrationFullAfterLeave } from '../../../../lib/services/open-meeting-registration/open-meeting-registration';
import {
  isAudienceCapacityEnabled,
  resolveRegisterJoinStatus,
} from '../../../../lib/helpers/openMeetingRegistrationHelper';
import BookingResultModal from '../../BookAMeetingWrapper/BookingResultModal';
import { RegistrationResultType } from '../../../../hooks/use-booking-subscription';
import { FailureType } from '../../../../lib/constants';

import './CardButtons.scss';

interface IProps {
  meeting: PrivateMeetingSessionDetailsResponseType;
  event: EventType;
  preRegistrationEnabled: boolean;
  isFeatured?: boolean
}

const CardButtons: React.FC<IProps> = (props: IProps): JSX.Element | null => {
  const {
    meeting,
    event,
    preRegistrationEnabled,
    isFeatured = !!props.isFeatured,
  } = props;

  const {
    access,
    meetingId,
    joinStatus,
    startDateTime,
    audienceCapacityEnabled,
  } = meeting;

  const { eventId, name, eventTitleForPassport } = event;

  const { leaveMeeting } = API[PROTECTED];
  const { register, isLoading } = useOpenMeetingRegistration();
  const isCapEnabled = isAudienceCapacityEnabled(audienceCapacityEnabled);
  const isRegistering = isCapEnabled && isLoading;

  const [status, setJoinStatus] = useState(joinStatus);
  const [showRegistrationErrorModal, setShowRegistrationErrorModal] = useState(false);
  const [registrationErrorResult, setRegistrationErrorResult] = useState<RegistrationResultType | null>(null);
  const mutationInFlight = useRef(false);

  useEffect(() => {
    if (mutationInFlight.current) {
      return;
    }
    setJoinStatus(joinStatus);
  }, [joinStatus]);

  const effectiveJoinStatus = resolveRegisterJoinStatus(status);

  const changeJoinStatus = (newJoinStatus: string) => {
    meeting.joinStatus = newJoinStatus;
    setJoinStatus(newJoinStatus);
  };

  const showRegistrationError = (result: RegistrationResultType) => {
    setRegistrationErrorResult(result);
    setShowRegistrationErrorModal(true);
  };

  const handleRegistrationUnavailable = () => {
    changeJoinStatus(JOIN_STATUS.REGISTRATION_FULL);
    showRegistrationError(RegistrationResultType.UNAVAILABLE);
  };

  const handleRegistrationFailed = () => {
    showRegistrationError(RegistrationResultType.FAILED);
  };

  const toggleStatusOnCard = () => {
    const oldJoinStatus = status ?? JOIN_STATUS.NOT_JOINED;
    const eventIdString = event.eventId.toString();

    switch (oldJoinStatus) {
    case JOIN_STATUS.REGISTRATION_FULL:
      return;

    case JOIN_STATUS.NOT_JOINED:
      if (isRegistering || mutationInFlight.current) {
        return;
      }

      mutationInFlight.current = true;

      if (!isCapEnabled) {
        changeJoinStatus(JOIN_STATUS.JOINED_AS_REGISTRANT);
      }

      register(eventIdString, meetingId, { audienceCapacityEnabled })
        .then(({ outcome }) => {
          if (outcome === 'registered') {
            if (isCapEnabled) {
              changeJoinStatus(JOIN_STATUS.JOINED_AS_REGISTRANT);
            }
            return;
          }
          if (outcome === 'unavailable') {
            handleRegistrationUnavailable();
            return;
          }
          if (outcome === 'failed') {
            handleRegistrationFailed();
            return;
          }
          if (!isCapEnabled) {
            changeJoinStatus(oldJoinStatus);
          }
        })
        .catch(() => changeJoinStatus(oldJoinStatus))
        .finally(() => {
          mutationInFlight.current = false;
        });
      return;

    case JOIN_STATUS.JOINED_AS_REGISTRANT:
      if (isRegistering || mutationInFlight.current) {
        return;
      }

      mutationInFlight.current = true;
      changeJoinStatus(JOIN_STATUS.NOT_JOINED);

      leaveMeeting(eventIdString, meetingId)
        .then(async () => {
          if (isCapEnabled && await isMeetingRegistrationFullAfterLeave(eventIdString, meetingId, 'replays')) {
            changeJoinStatus(JOIN_STATUS.REGISTRATION_FULL);
          }
        })
        .catch(() => changeJoinStatus(oldJoinStatus))
        .finally(() => {
          mutationInFlight.current = false;
        });
      return;

    default:
      return;
    }
  };

  const isConnectBtnDisabled = useConnectMeetingDisabled(event, startDateTime);

  const showJoinViewButtons = (): JSX.Element | null => {
    if (showJoinButton(access, preRegistrationEnabled, effectiveJoinStatus)) {
      const eventIdString = event.eventId.toString();
      return isConnectBtnDisabled
        ? <ViewDetailsButton eventId={eventIdString} meetingId={meetingId}/>
        : <JoinButton eventId={eventIdString} meetingId={meetingId}/>;
    } return null;
  };

  const showRegisterButtons = (className?: string): JSX.Element | null => {
    if (access === 'OPEN') {
      return (
        <RegisterButton
          joinStatus={status as JOIN_STATUS}
          toggleStatusOnCard={toggleStatusOnCard}
          isLoading={isRegistering}
        />
      );
    }
    if (className) {
      return <div className={className}/>;
    }
    return null;
  };

  const showCalendarButtons = (): JSX.Element | null => {
    if (
      access === 'PLENARY' ||
      (effectiveJoinStatus !== JOIN_STATUS.NOT_JOINED &&
        effectiveJoinStatus !== JOIN_STATUS.REGISTRATION_FULL)
    ) {
      return (
        <AddToCalendarButton
          eventId={eventId}
          name={name}
          isFeatured={isFeatured}
          meetingId={meetingId}
          eventTitleForPassport={eventTitleForPassport || ''}
        />);
    } return null;
  };

  const showMeetingStatus = (): JSX.Element | null => {
    return <MeetingStatus startDateTime={startDateTime} className='card-buttons__featured-wrapper__status' />;
  };

  const content = !isFeatured
    ? (
      <div className='card-buttons card-buttons__non-featured-wrapper'>
        <div className='card-buttons__non-featured-wrapper__top-block'>
          {showJoinViewButtons()}
          {showCalendarButtons()}
        </div>
        {showRegisterButtons('base-register base-register__disabled')}
      </div>
    )
    : (
      <div className='card-buttons'>
        <div className='card-buttons__featured-wrapper'>
          {showRegisterButtons()}
          {showJoinViewButtons()}
          {showCalendarButtons()}
          {showMeetingStatus()}
        </div>
      </div>
    );

  return (
    <>
      {content}
      <BookingResultModal
        isOpen={showRegistrationErrorModal}
        onClose={() => {
          setShowRegistrationErrorModal(false);
          setRegistrationErrorResult(null);
        }}
        slot={null}
        speakers={[]}
        showLocalTime={false}
        eventTimeZone={event.timeZone || ''}
        result={registrationErrorResult ?? RegistrationResultType.FAILED}
        failureType={
          registrationErrorResult === RegistrationResultType.UNAVAILABLE
            ? FailureType.OPEN_MEETING_FULL
            : undefined
        }
      />
    </>
  );
};

export default CardButtons;
